import {
  BusinessEntityType,
  EmployerDocumentDto,
  USState,
} from '@zorro/clients';
import {
  VALIDATION_MESSAGES,
  optionalNumber,
  stateNameByCode,
} from '@zorro/common-utils';
import { YesNo } from '@zorro/types';
import * as yup from 'yup';

import { getPersonFormSchema } from '../PersonFormArray';
import { taxIdExclusions, taxIdSchemaObject } from '../TaxIdInput';

const baseCompanyDetailsSchema = {
  legalName: yup.string(),
  address: yup.string(),
  businessEntityType: yup
    .mixed<BusinessEntityType>()
    .oneOf(
      Object.values(BusinessEntityType),
      VALIDATION_MESSAGES.businessEntityType
    )
    .typeError(VALIDATION_MESSAGES.businessEntityType)
    .nullable(),
  taxId: taxIdSchemaObject.taxId,
  taxIdType: taxIdSchemaObject.taxIdType,
  logo: yup.mixed<File | string>(),
  naicsCode: yup.string().nullable(),
  stateOfIncorporation: yup
    .mixed<USState>()
    .oneOf(Object.values(USState))
    .nullable(),
  yearsInBusiness: yup
    .number()
    .transform(optionalNumber)
    .nullable()
    .integer()
    .min(1, VALIDATION_MESSAGES.yearsInBusinessMin),
  hasBeneficialOwners: yup.mixed<YesNo>().oneOf(Object.values(YesNo)),
  beneficialOwners: yup
    .array()
    .of(getPersonFormSchema(false))
    .max(4, VALIDATION_MESSAGES.beneficialOwnersMax),
};

const getFinalizationModeCompanyDetailsSchema = (
  isLynxPaymentsEnabled: boolean
) => {
  const beneficialOwnerSchema = getPersonFormSchema(
    true,
    isLynxPaymentsEnabled
  );

  return {
    legalName: yup.string().when('isLegalNameSameAsCompanyName', {
      is: false,
      then: () => yup.string().required(VALIDATION_MESSAGES.legalNameRequired),
      otherwise: () => yup.string().notRequired(),
    }),
    address: yup.string().required(VALIDATION_MESSAGES.addressRequired),
    businessEntityType: yup
      .mixed<BusinessEntityType>()
      .oneOf(
        Object.values(BusinessEntityType),
        VALIDATION_MESSAGES.businessEntityType
      )
      .typeError(VALIDATION_MESSAGES.businessEntityType)
      .required(VALIDATION_MESSAGES.businessEntityType),
    taxId: taxIdSchemaObject.taxId.required(
      VALIDATION_MESSAGES.taxIdTypeRequired
    ),
    taxIdType: taxIdSchemaObject.taxIdType.required(
      VALIDATION_MESSAGES.taxIdTypeRequired
    ),
    logo: yup
      .mixed<File | string>()
      .required(VALIDATION_MESSAGES.logoUrlRequired),
    naicsCode: yup
      .string()
      .typeError(VALIDATION_MESSAGES.naicsCode)
      .required(VALIDATION_MESSAGES.naicsCode),
    stateOfIncorporation: isLynxPaymentsEnabled
      ? yup
          .mixed<USState>()
          .oneOf(Object.values(USState))
          .nullable()
          .required(VALIDATION_MESSAGES.stateOfIncorporationRequired)
      : yup.mixed<USState>().oneOf(Object.values(USState)).nullable(),
    yearsInBusiness: isLynxPaymentsEnabled
      ? yup
          .number()
          .transform(optionalNumber)
          .integer()
          .min(1, VALIDATION_MESSAGES.yearsInBusinessMin)
          .required(VALIDATION_MESSAGES.yearsInBusinessRequired)
      : yup
          .number()
          .transform(optionalNumber)
          .nullable()
          .integer()
          .min(1, VALIDATION_MESSAGES.yearsInBusinessMin),
    hasBeneficialOwners: isLynxPaymentsEnabled
      ? yup
          .mixed<YesNo>()
          .oneOf(Object.values(YesNo))
          .required(VALIDATION_MESSAGES.hasBeneficialOwnersRequired)
      : yup.mixed<YesNo>().oneOf(Object.values(YesNo)),
    beneficialOwners: isLynxPaymentsEnabled
      ? yup
          .array()
          .of(beneficialOwnerSchema)
          .when('hasBeneficialOwners', {
            is: YesNo.YES,
            then: (schema) =>
              schema.min(1, VALIDATION_MESSAGES.beneficialOwnersRequired),
          })
          .max(4, VALIDATION_MESSAGES.beneficialOwnersMax)
          .required(VALIDATION_MESSAGES.beneficialOwnersRequired)
      : yup
          .array()
          .of(beneficialOwnerSchema)
          .max(4, VALIDATION_MESSAGES.beneficialOwnersMax),
  };
};

export const getCompanyDetailsSchemaObject = (
  isFinalizationMode: boolean,
  isSignedAgreementRequired: boolean,
  isLynxPaymentsEnabled: boolean
) => {
  return yup.object().shape(
    {
      name: yup.string().required(VALIDATION_MESSAGES.companyNameRequired),
      isLegalNameSameAsCompanyName: yup
        .boolean()
        .required(VALIDATION_MESSAGES.isLegalNameSameAsCompanyName),
      websiteUrl: yup
        .string()
        .url(VALIDATION_MESSAGES.websiteUrlValid)
        .max(255, VALIDATION_MESSAGES.websiteUrlMax),
      isMailingAddressSameAsHQAddress: yup
        .boolean()
        .required(VALIDATION_MESSAGES.isMailingAddressSameAsHQAddress),
      mailingAddress: yup.string().when('isMailingAddressSameAsHQAddress', {
        is: false,
        then: () =>
          yup.string().required(VALIDATION_MESSAGES.companyMailingAddress),
        otherwise: () => yup.string().notRequired(),
      }),
      ...(isSignedAgreementRequired
        ? {
            signedAgreement: yup.mixed<File | EmployerDocumentDto>().required(),
            agreementSignedDate: yup
              .date()
              .required(VALIDATION_MESSAGES.dateAgreementSignedRequired),
          }
        : {
            signedAgreement: yup
              .mixed<File | EmployerDocumentDto>()
              .notRequired(),
            agreementSignedDate: yup.date(),
          }),
      ...(isFinalizationMode
        ? getFinalizationModeCompanyDetailsSchema(isLynxPaymentsEnabled)
        : baseCompanyDetailsSchema),
    },
    taxIdExclusions
  );
};

export type CompanyDetailsFormFields = yup.InferType<
  ReturnType<typeof getCompanyDetailsSchemaObject>
>;

export const stateSelectOptions = Object.values(USState).map((code) => ({
  value: code,
  label: stateNameByCode[code] ?? code,
}));

export const hasBeneficialOwnersDescriptions: Record<YesNo, string> = {
  [YesNo.YES]: 'at least one person owns 25% or more',
  [YesNo.NO]: 'no individual owns 25% or more',
};
